import React, { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp, Search, Users, Layers, Crosshair, Check, Loader2, Sparkles } from "lucide-react";
import { sr } from "../../lib/sr";
import ReachMap from "./ReachMap";

const EMPTY = { mode: "audience", conquesting: false, audienceIds: [], audienceNames: [], contextualIds: [], contextualNames: [] };

const MODES = [
  { key: "audience", label: "Custom audience", icon: Users, hint: "Search real data audiences and see the reach in your area." },
  { key: "contextual", label: "Contextual", icon: Layers, hint: "Place ads next to content about your product & services." },
  { key: "competitor", label: "Competitor", icon: Crosshair, hint: "Win customers around your competitors' locations." },
];

// Reusable Advanced setup block: pick ONE targeting approach — custom audience search,
// contextual search (auto-suggested from the business), or competitor conquesting.
// `geo` (optional) scopes the modeled reach estimate. Calls onChange with the full selection.
export default function AdvancedTargeting({ geo = null, value, onChange, defaultOpen = false }) {
  const v = { ...EMPTY, ...(value || {}) };
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null); // { matches, ai_summary, geo_label, auto_suggested }

  const patch = (p) => onChange({ ...v, ...p });
  const geoKey = JSON.stringify(geo || {});

  const runSearch = useCallback(async (mode, q) => {
    setLoading(true);
    try {
      const data = mode === "contextual"
        ? await sr.contextualTargetSearch(q || "", geo)
        : await sr.audienceTargetSearch(q || "", geo);
      setRes(data);
    } catch { setRes({ matches: [], ai_summary: "Search unavailable right now — try again." }); }
    finally { setLoading(false); }
  }, [geoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-suggest contextual on first open of that tab; re-scope reach when geo changes.
  useEffect(() => {
    if (!open) return;
    if (v.mode === "contextual" && !res) runSearch("contextual", "");
    if (res && (v.mode === "audience" || v.mode === "contextual")) runSearch(v.mode, res.query || query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoKey, v.mode, open]);

  const togglePick = (m) => {
    if (v.mode === "contextual") {
      const on = v.contextualIds.includes(m.id);
      patch({
        contextualIds: on ? v.contextualIds.filter((x) => x !== m.id) : [...v.contextualIds, m.id],
        contextualNames: on ? v.contextualNames.filter((x) => x !== m.name) : [...v.contextualNames, m.name],
      });
    } else {
      const on = v.audienceIds.includes(m.id);
      patch({
        audienceIds: on ? v.audienceIds.filter((x) => x !== m.id) : [...v.audienceIds, m.id],
        audienceNames: on ? v.audienceNames.filter((x) => x !== m.name) : [...v.audienceNames, m.name],
      });
    }
  };
  const isPicked = (m) => (v.mode === "contextual" ? v.contextualIds : v.audienceIds).includes(m.id);
  const pickedCount = v.audienceIds.length + v.contextualIds.length + (v.conquesting ? 1 : 0);

  const setMode = (mode) => { patch({ mode }); setRes(null); setQuery(""); };

  return (
    <div className="sr-card" data-testid="advanced-targeting" style={{ padding: 0, overflow: "hidden" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="advanced-targeting-toggle"
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "14px 16px", background: "none", border: "none", cursor: "pointer" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={15} color="var(--blue)" />
          <strong style={{ color: "var(--navy)" }}>Advanced setup</strong>
          <span className="sr-muted" style={{ fontSize: 12 }}>· audience &amp; contextual search{pickedCount > 0 ? ` · ${pickedCount} selected` : ""}</span>
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <ReachMap radiusMiles={geo?.radius_miles} />
          <div className="sr-muted" style={{ fontSize: 13, marginBottom: 8 }}>How do you want to target?</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            {MODES.map((m) => {
              const on = v.mode === m.key;
              const Ico = m.icon;
              return (
                <button type="button" key={m.key} onClick={() => setMode(m.key)} data-testid={`targeting-mode-${m.key}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  <Ico size={14} /> {m.label}
                </button>
              );
            })}
          </div>
          <p className="sr-muted" style={{ fontSize: 12, margin: "8px 0 12px" }}>{MODES.find((m) => m.key === v.mode)?.hint}</p>

          {v.mode === "competitor" && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }} data-testid="targeting-conquesting-label">
              <input type="checkbox" checked={v.conquesting} onChange={(e) => patch({ conquesting: e.target.checked })} data-testid="targeting-conquesting" style={{ marginTop: 3 }} />
              <span>
                <strong style={{ color: "var(--navy)" }}>Competitor Conquesting</strong>
                <div className="sr-muted" style={{ fontSize: 12 }}>Target audiences around your mapped competitor locations to win their customers.</div>
              </span>
            </label>
          )}

          {(v.mode === "audience" || v.mode === "contextual") && (
            <div>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="sr-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(v.mode, query); } }}
                  data-testid={`targeting-search-input-${v.mode}`}
                  placeholder={v.mode === "audience" ? "Describe who to reach — e.g. homeowners 35-54, pet owners, new movers" : "Topic — e.g. local news, sports, cooking (or leave blank to auto-suggest)"}
                  style={{ flex: 1 }} />
                <button type="button" className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => runSearch(v.mode, query)} disabled={loading} data-testid={`targeting-search-btn-${v.mode}`}>
                  {loading ? <Loader2 size={14} className="sr-spin" /> : <><Search size={14} /> Search</>}
                </button>
              </div>

              {res && (
                <div style={{ marginTop: 10 }} data-testid={`targeting-results-${v.mode}`}>
                  {res.geo_label && <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Reach shown for <strong>{res.geo_label}</strong> · planning estimate</div>}
                  {res.auto_suggested && <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Auto-suggested from your product &amp; services.</div>}
                  {(res.matches || []).length === 0 && <div className="sr-muted" style={{ fontSize: 13 }}>{res.ai_summary}</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(res.matches || []).map((m) => {
                      const on = isPicked(m);
                      return (
                        <button type="button" key={m.id} onClick={() => togglePick(m)} data-testid={`targeting-pick-${m.id}`}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, textAlign: "left", border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue-light, #EEF4FF)" : "#fff", borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {on && <Check size={14} color="var(--blue)" />}
                              <strong style={{ color: "var(--navy)", fontSize: 13 }}>{m.name}</strong>
                              <span className="sr-muted" style={{ fontSize: 11 }}>· {m.provider}</span>
                            </span>
                            {m.match_reason && <span className="sr-muted" style={{ fontSize: 11, display: "block", marginTop: 2 }}>{m.match_reason}</span>}
                          </span>
                          <span style={{ flexShrink: 0, textAlign: "right" }}>
                            <span style={{ fontWeight: 800, color: "var(--blue)", fontSize: 13 }} data-testid={`targeting-reach-${m.id}`}>{m.geo_reach_label}</span>
                            <span className="sr-muted" style={{ fontSize: 10, display: "block" }}>of {Number(m.national_reach).toLocaleString()} national</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Build a compact human summary of the selected targeting for the AI prompt.
export function targetingSummary(v) {
  if (!v) return null;
  const parts = [];
  if (v.audienceNames?.length) parts.push(`Audiences: ${v.audienceNames.join(", ")}`);
  if (v.contextualNames?.length) parts.push(`Contextual: ${v.contextualNames.join(", ")}`);
  if (v.conquesting) parts.push("Competitor conquesting enabled");
  return parts.join(" · ") || null;
}
